import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SEO from "@/components/app/SEO";

const CATEGORIES = [
  { value: "livraison", label: "Non-réception du courrier / retard postal" },
  { value: "paiement", label: "Paiement / facturation" },
  { value: "remboursement", label: "Demande de remboursement" },
  { value: "qualite", label: "Qualité de la prestation" },
  { value: "donnees", label: "Données personnelles" },
  { value: "autre", label: "Autre" },
];

export default function ReclamationPage() {
  const [form, setForm] = useState({ case_id: "", first_name: "", last_name: "", email: "", category: "livraison", subject: "", message: "" });
  const [sent, setSent] = useState(null); // {ticket_id}

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/reclamations", form);
      toast.success("Réclamation enregistrée");
      setSent(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <SEO title="Déposer une réclamation" description="Déposez une réclamation officielle auprès de Résiliation Express — accusé de réception automatique." canonical="/reclamation" />
      <h1 className="text-4xl font-bold tracking-tight mb-2">Déposer une réclamation</h1>
      <p className="text-muted-foreground mb-4">Nous accusons réception automatiquement et vous répondons sous 48h ouvrées. En cas de litige non résolu, vous pouvez saisir gratuitement le médiateur de la consommation.</p>

      <section id="mediateur" className="mb-8 rounded-md border border-border p-4 bg-secondary/40 text-sm" data-testid="reclamation-mediateur">
        <h2 className="font-semibold mb-2">Médiation de la consommation</h2>
        <p className="leading-relaxed">
          Conformément aux articles L. 612-1 et suivants du Code de la consommation, en cas de litige non résolu après une réclamation
          écrite préalable auprès de Résiliation Express, le consommateur peut recourir gratuitement au médiateur de la consommation
          dont nous relevons : <strong>Société Médiation Professionnelle</strong>. La saisine peut être effectuée en ligne sur
          {" "}<a href="https://www.mediateur-consommation-smp.fr/" target="_blank" rel="noopener noreferrer" className="text-primary">https://www.mediateur-consommation-smp.fr/</a>
          {" "}ou par courrier à Société de la Médiation Professionnelle, Alteritae, 5 rue Salvaing, 12000 Rodez.
        </p>
      </section>

      {sent ? (
        <div className="card-elevated p-8 space-y-3" data-testid="reclamation-success">
          <div className="text-xl font-semibold text-green-600">Réclamation enregistrée</div>
          <p className="text-sm text-muted-foreground">Identifiant unique de votre réclamation :</p>
          <div className="font-mono text-lg bg-secondary/50 rounded-md p-3 select-all" data-testid="reclamation-id">{sent.ticket_id}</div>
          <p className="text-sm text-muted-foreground">Conservez cet identifiant. Un accusé de réception vous sera envoyé par email si nous en disposons.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 card-elevated p-6" data-testid="reclamation-form">
          <div>
            <Label htmlFor="rc-case">Numéro de dossier (si applicable)</Label>
            <Input id="rc-case" value={form.case_id} onChange={upd("case_id")} placeholder="Ex : abc12345" data-testid="rc-case-id" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rc-first">Prénom</Label>
              <Input id="rc-first" value={form.first_name} onChange={upd("first_name")} required data-testid="rc-first" />
            </div>
            <div>
              <Label htmlFor="rc-last">Nom</Label>
              <Input id="rc-last" value={form.last_name} onChange={upd("last_name")} required data-testid="rc-last" />
            </div>
          </div>
          <div>
            <Label htmlFor="rc-email">Email</Label>
            <Input id="rc-email" type="email" value={form.email} onChange={upd("email")} required data-testid="rc-email" />
          </div>
          <div>
            <Label htmlFor="rc-cat">Catégorie</Label>
            <select id="rc-cat" value={form.category} onChange={upd("category")} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="rc-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="rc-subject">Objet</Label>
            <Input id="rc-subject" value={form.subject} onChange={upd("subject")} required data-testid="rc-subject" />
          </div>
          <div>
            <Label htmlFor="rc-msg">Description détaillée</Label>
            <Textarea id="rc-msg" rows={6} value={form.message} onChange={upd("message")} required minLength={30} data-testid="rc-message" />
            <p className="text-xs text-muted-foreground mt-1">Minimum 30 caractères. N'incluez pas d'informations bancaires ou de mots de passe.</p>
          </div>
          <Button type="submit" className="w-full bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="rc-submit-btn">
            Envoyer la réclamation
          </Button>
        </form>
      )}
    </div>
  );
}
